
import React, { useState, useMemo } from 'react';
import { MindMapNode } from '../types';
import { X, CheckSquare, Square, Image as ImageIcon, Type, GraduationCap, CheckCircle2, Hash, Map as MapIcon, List as ListIcon, Loader2 } from 'lucide-react';

interface QuizSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: MindMapNode[];
  onConfirm: (selectedNodeIds: string[], questionCount: number) => void;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
  isLoading?: boolean; // NEW: Trạng thái đang tải
}

export const QuizSelectionModal: React.FC<QuizSelectionModalProps> = ({
  isOpen,
  onClose,
  nodes,
  onConfirm,
  selectedIds: externalSelectedIds,
  onToggle: externalOnToggle,
  isLoading = false
}) => {
  const validNodes = useMemo(() => {
    return nodes.filter(n => (n.content && n.content.length > 2) || n.imageBase64);
  }, [nodes]);

  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set(validNodes.map(n => n.id)));
  const selectedIds = externalSelectedIds || internalSelectedIds;
  
  const [questionCount, setQuestionCount] = useState<number>(5);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  const toggleNode = (id: string) => {
    if (isLoading) return;
    if (externalOnToggle) {
        externalOnToggle(id);
    } else {
        const newSet = new Set(internalSelectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setInternalSelectedIds(newSet);
    }
  };

  const toggleAll = () => {
    if (isLoading) return;
    if (externalOnToggle) {
        const allSelected = selectedIds.size === validNodes.length;
        validNodes.forEach(n => {
            if (allSelected) {
                if (selectedIds.has(n.id)) externalOnToggle(n.id);
            } else {
                if (!selectedIds.has(n.id)) externalOnToggle(n.id);
            }
        });
    } else {
        if (selectedIds.size === validNodes.length) {
            setInternalSelectedIds(new Set());
        } else {
            setInternalSelectedIds(new Set(validNodes.map(n => n.id)));
        }
    }
  };

  const handleConfirm = () => {
    if (isLoading) return;
    onConfirm(Array.from(selectedIds), questionCount);
  };

  if (!isOpen) return null;

  // --- MAP MODE (MINIMIZED BAR) ---
  if (viewMode === 'map') {
      return (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-4 w-full px-4 pointer-events-none">
              <div className="bg-black/70 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-in slide-in-from-bottom-5 pointer-events-auto">
                  Tap nodes on the map to select
              </div>

              <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 shadow-2xl rounded-2xl p-2 flex items-center gap-3 pointer-events-auto animate-in slide-in-from-bottom-5">
                  <button 
                      onClick={() => !isLoading && setViewMode('list')}
                      disabled={isLoading}
                      className="p-3 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                      <ListIcon size={18} />
                      <span className="hidden sm:inline">Review List ({selectedIds.size})</span>
                      <span className="sm:hidden">List ({selectedIds.size})</span>
                  </button>

                  <div className="h-8 w-px bg-gray-200 dark:bg-slate-700"></div>

                  <div className="flex items-center gap-2 px-2">
                        <span className="text-xs font-bold text-gray-500 uppercase">Qs:</span>
                        <input 
                            type="number" 
                            min="1" 
                            max="20" 
                            value={questionCount} 
                            disabled={isLoading}
                            onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                            className="w-12 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-1 py-1 text-center text-sm font-bold focus:ring-2 focus:ring-purple-500 outline-none disabled:opacity-50"
                        />
                  </div>

                  <button 
                      onClick={handleConfirm}
                      disabled={selectedIds.size === 0 || isLoading}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-sm hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                      {isLoading ? <Loader2 size={18} className="animate-spin" /> : <GraduationCap size={18} />}
                      {isLoading ? 'Generating...' : 'Generate'}
                  </button>
              </div>
          </div>
      );
  }

  // --- LIST MODE (FULL MODAL) ---
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 bg-gray-50 dark:bg-slate-950">
           <div className="flex items-center gap-3">
               <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg">
                   <GraduationCap size={20} />
               </div>
               <div>
                   <h3 className="font-bold text-lg dark:text-white">Configure Quiz</h3>
                   <p className="text-xs text-gray-500 dark:text-gray-400">Select content source.</p>
               </div>
           </div>
           <button 
             onClick={onClose}
             disabled={isLoading}
             className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500 disabled:opacity-30"
           >
             <X size={20} />
           </button>
        </div>

        {/* Settings Toolbar */}
        <div className="px-6 py-4 border-b dark:border-slate-800 bg-white dark:bg-slate-900 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 min-w-[90px]">
                        <Hash size={16} className="text-purple-500" /> 
                        <span>Questions:</span>
                    </div>
                    <input 
                        type="range" 
                        min="1" 
                        max="20" 
                        value={questionCount} 
                        disabled={isLoading}
                        onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-600 disabled:opacity-50"
                    />
                    <span className="text-sm font-bold w-8 text-center bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                        {questionCount}
                    </span>
                </div>

                <button 
                    onClick={() => !isLoading && setViewMode('map')}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold text-sm rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-200 dark:border-blue-900/50 disabled:opacity-50"
                >
                    <MapIcon size={16} /> Select on Map
                </button>
            </div>

            <div className="h-px bg-gray-100 dark:bg-slate-800"></div>

            <div className="flex justify-between items-center">
                <button 
                    onClick={toggleAll}
                    disabled={isLoading}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                >
                    {selectedIds.size === validNodes.length ? (
                        <>
                            <CheckSquare size={16} className="text-blue-600" /> Deselect All
                        </>
                    ) : (
                        <>
                            <Square size={16} /> Select All
                        </>
                    )}
                </button>
                <span className="text-xs font-bold px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full">
                    {selectedIds.size} notes selected
                </span>
            </div>
        </div>

        {/* List Area */}
        <div className={`flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-slate-950 space-y-2 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
            {validNodes.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                    <p>No valid notes found with content or images.</p>
                </div>
            ) : (
                validNodes.map(node => {
                    const isSelected = selectedIds.has(node.id);
                    return (
                        <div 
                            key={node.id}
                            onClick={() => toggleNode(node.id)}
                            className={`flex items-center gap-4 p-3 rounded-xl border cursor-pointer transition-all ${
                                isSelected 
                                ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 shadow-sm' 
                                : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800 hover:border-gray-300 dark:hover:border-slate-700'
                            }`}
                        >
                            <div className={`flex-shrink-0 text-blue-600 dark:text-blue-500 transition-transform ${isSelected ? 'scale-110' : 'opacity-40 scale-100'}`}>
                                {isSelected ? <CheckCircle2 size={22} fill="currentColor" className="text-white dark:text-slate-900" /> : <Square size={22} />}
                            </div>

                            {node.imageBase64 && (
                                <div className="w-12 h-12 flex-shrink-0 bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                                    <img src={node.imageBase64} alt="" className="w-full h-full object-cover" />
                                </div>
                            )}

                            <div className="flex-1 min-w-0">
                                {node.title && (
                                    <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200 truncate mb-0.5">
                                        {node.title}
                                    </h4>
                                )}
                                <div className="flex items-center gap-2">
                                    {!node.imageBase64 && <Type size={12} className="text-gray-400" />}
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {node.content || (node.imageBase64 ? "Image only" : "Empty note")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-end gap-3">
            <button 
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
            >
                Cancel
            </button>
            <button 
                onClick={handleConfirm}
                disabled={selectedIds.size === 0 || isLoading}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg transition-colors shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <GraduationCap size={18} />}
                {isLoading ? 'Generating Quiz...' : `Generate Quiz (${questionCount} Qs)`}
            </button>
        </div>
      </div>
    </div>
  );
};
